import Phaser from "phaser";
import { fishData, FishData, spawnFrequencyWeights } from "../../data/fishData";
import { BassAI } from "./BassAI";
import { FishBase } from "./FishBase";
import { MuskyAI } from "./MuskyAI";
import { PanfishAI } from "./PanfishAI";
import { PikeAI } from "./PikeAI";
import { TroutAI } from "./TroutAI";
import { WalleyeAI } from "./WalleyeAI";
import { FISH_DESPAWN_BUFFER_PX, FISH_EDGE_SPAWN_CORNER_PADDING_PX, FISH_EDGE_SPAWN_INSET_PX } from "../../game/constants";

interface FishSpawnerConfig {
    scene: Phaser.Scene;
    fishGroup: Phaser.GameObjects.Group;
    targetCount: number;
    worldWidth: number;
    worldHeight: number;
    player: Phaser.Physics.Arcade.Sprite;
    speciesWeightMultipliers?: Record<string, number>;
}

export class FishSpawner {
    private static readonly NEAR_PLAYER_SPAWN_CHANCE = 0.35;
    private static readonly NEAR_PLAYER_MIN_DISTANCE = 180;
    private static readonly NEAR_PLAYER_MAX_DISTANCE = 620;
    private static readonly INTERIOR_SPAWN_MARGIN = 60;

    private readonly scene: Phaser.Scene;
    private readonly fishGroup: Phaser.GameObjects.Group;
    private readonly targetCount: number;
    private readonly worldWidth: number;
    private readonly worldHeight: number;
    private readonly player: Phaser.Physics.Arcade.Sprite;
    private readonly speciesWeightMultipliers: Record<string, number>;
    private frenzyMultiplier = 1;

    constructor(config: FishSpawnerConfig) {
        this.scene = config.scene;
        this.fishGroup = config.fishGroup;
        this.targetCount = config.targetCount;
        this.worldWidth = config.worldWidth;
        this.worldHeight = config.worldHeight;
        this.player = config.player;
        this.speciesWeightMultipliers = config.speciesWeightMultipliers ?? {};
    }

    seed(): void {
        const count = Math.floor(this.targetCount * this.frenzyMultiplier);
        for (let i = 0; i < count; i += 1) {
            this.fishGroup.add(this.createRandomFish());
        }
    }

    update(): void {
        const target = Math.floor(this.targetCount * this.frenzyMultiplier);
        while (this.fishGroup.getLength() < target) {
            this.fishGroup.add(this.createRandomFish());
        }
    }

    setFrenzyMultiplier(multiplier: number): void {
        this.frenzyMultiplier = Math.max(1, multiplier);
    }

    wrapOrCull(fish: FishBase): void {
        if (fish.x < -FISH_DESPAWN_BUFFER_PX || fish.x > this.worldWidth + FISH_DESPAWN_BUFFER_PX || fish.y < -FISH_DESPAWN_BUFFER_PX || fish.y > this.worldHeight + FISH_DESPAWN_BUFFER_PX) {
            this.fishGroup.remove(fish, true, true);
        }
    }

    private createRandomFish(): FishBase {
        const fishSpec = this.pickByWeight(fishData);
        const spawn = this.getSpawnPoint();

        switch (fishSpec.fishName) {
            case "Bluegill":
            case "Crappie":
            case "Perch":
                return new PanfishAI(this.scene, spawn.x, spawn.y, fishSpec.fishName, fishSpec.textureKey);
            case "Largemouth Bass":
            case "Smallmouth Bass":
                return new BassAI(this.scene, spawn.x, spawn.y, fishSpec.fishName, fishSpec.textureKey);
            case "Walleye":
                return new WalleyeAI(this.scene, spawn.x, spawn.y);
            case "Trout":
                return new TroutAI(this.scene, spawn.x, spawn.y);
            case "Northern Pike":
                return new PikeAI(this.scene, spawn.x, spawn.y);
            case "Musky":
                return new MuskyAI(this.scene, spawn.x, spawn.y, this.player);
            default:
                return new TroutAI(this.scene, spawn.x, spawn.y);
        }
    }

    private getSpawnPoint(): Phaser.Math.Vector2 {
        if (Math.random() < FishSpawner.NEAR_PLAYER_SPAWN_CHANCE) {
            return this.getNearPlayerSpawnPoint();
        }
        return this.getEdgeSpawnPoint();
    }

    private getNearPlayerSpawnPoint(): Phaser.Math.Vector2 {
        const angle = Phaser.Math.FloatBetween(0, Math.PI * 2);
        const distance = Phaser.Math.FloatBetween(FishSpawner.NEAR_PLAYER_MIN_DISTANCE, FishSpawner.NEAR_PLAYER_MAX_DISTANCE);
        const x = this.player.x + Math.cos(angle) * distance;
        const y = this.player.y + Math.sin(angle) * distance;
        return new Phaser.Math.Vector2(this.clampToInterior(x, this.worldWidth), this.clampToInterior(y, this.worldHeight));
    }

    private clampToInterior(value: number, limit: number): number {
        return Phaser.Math.Clamp(value, FishSpawner.INTERIOR_SPAWN_MARGIN, limit - FishSpawner.INTERIOR_SPAWN_MARGIN);
    }

    private pickByWeight(items: FishData[]): FishData {
        const weighted = items.map((item) => ({
            item,
            weight: spawnFrequencyWeights[item.spawnFrequency] * (this.speciesWeightMultipliers[item.fishName] ?? 1),
        }));
        const total = weighted.reduce((sum, value) => sum + value.weight, 0);
        let roll = Math.random() * total;
        for (const entry of weighted) {
            roll -= entry.weight;
            if (roll <= 0) {
                return entry.item;
            }
        }
        return weighted[weighted.length - 1].item;
    }

    private getEdgeSpawnPoint(): Phaser.Math.Vector2 {
        const inset = FISH_EDGE_SPAWN_INSET_PX;
        const horizontalMin = Math.min(FISH_EDGE_SPAWN_CORNER_PADDING_PX, this.worldWidth / 2);
        const horizontalMax = Math.max(this.worldWidth - FISH_EDGE_SPAWN_CORNER_PADDING_PX, horizontalMin);
        const verticalMin = Math.min(FISH_EDGE_SPAWN_CORNER_PADDING_PX, this.worldHeight / 2);
        const verticalMax = Math.max(this.worldHeight - FISH_EDGE_SPAWN_CORNER_PADDING_PX, verticalMin);
        const edge = Phaser.Math.Between(0, 3);
        switch (edge) {
            case 0:
                return new Phaser.Math.Vector2(inset, Phaser.Math.Between(verticalMin, verticalMax));
            case 1:
                return new Phaser.Math.Vector2(this.worldWidth - inset, Phaser.Math.Between(verticalMin, verticalMax));
            case 2:
                return new Phaser.Math.Vector2(Phaser.Math.Between(horizontalMin, horizontalMax), inset);
            default:
                return new Phaser.Math.Vector2(Phaser.Math.Between(horizontalMin, horizontalMax), this.worldHeight - inset);
        }
    }
}
